import React, { useState, useEffect } from 'react';
import axiosInstance from '../../config/axiosInstance';
import { toast } from 'react-toastify';
import { Search, Lock, Unlock, FileText } from 'lucide-react';
import useResponsivePageSize from '../hooks/useResponsivePageSize';
import { filterTransactions } from './AdminTransactions.utils';
import './AdminTransactions.css';

// Mock transaction data
const mockTransactions = [
  {
    id: 'txn001',
    userId: 'user1234567890',
    userName: 'John Doe',
    type: 'deposit',
    amount: 1500.0,
    currency: 'USDT',
    status: 'pending',
    method: 'TRC20',
    walletAddress: 'TQn9Y2khEsLJW1ChVWFMSMeRDow5oQHR7E',
    date: '2024-11-13 10:30',
    txHash: '0x1a2b3c4d5e6f...',
    fee: 1.0,
  },
  {
    id: 'txn002',
    userId: 'user0987654321',
    userName: 'Jane Smith',
    type: 'withdrawal',
    amount: 800.0,
    currency: 'BTC',
    status: 'completed',
    method: 'Bitcoin Network',
    walletAddress: 'bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh',
    date: '2024-11-12 15:45',
    txHash: '0x9f8e7d6c5b4a...',
    fee: 0.0005,
  },
  {
    id: 'txn003',
    userId: 'user5566778899',
    userName: 'Alice Brown',
    type: 'exchange',
    amount: 2000.0,
    currency: 'ETH to USDT',
    status: 'completed',
    method: 'Internal Exchange',
    walletAddress: 'Internal',
    date: '2024-11-11 09:20',
    txHash: 'exchange_internal',
    fee: 10.0,
  },
  {
    id: 'txn004',
    userId: 'user1122334455',
    userName: 'Bob Johnson',
    type: 'deposit',
    amount: 500.0,
    currency: 'ETH',
    status: 'rejected',
    method: 'ERC20',
    walletAddress: '0x742d35Cc6754C0532925a3b8D0C2d0E8b9c1a8e7',
    date: '2024-11-10 14:10',
    txHash: '0x5a4b3c2d1e0f...',
    fee: 0.001,
  },
];

const AdminTransactions = () => {
  const [transactions, setTransactions] = useState(mockTransactions);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = useResponsivePageSize();
  const [stats, setStats] = useState({
    totalDeposits: 0,
    totalWithdrawals: 0,
    totalExchanges: 0,
    pending: 0,
  });
  const [loading, setLoading] = useState(true);
  const [transactionActionLoading, setTransactionActionLoading] = useState(false);
  const [showLockModal, setShowLockModal] = useState(false);
  const [lockForm, setLockForm] = useState({
    uid: '',
    currencyCode: 'USDT',
    amount: '',
  });
  const [lockLoading, setLockLoading] = useState(false);
  const [walletBalances, setWalletBalances] = useState(null);
  const [fetchingBalances, setFetchingBalances] = useState(false);

  // Function to generate pagination numbers with ellipsis
  const getPaginationNumbers = () => {
    const pages = [];

    if (totalPages <= 3) {
      // Show all pages if 3 or less
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Check if we're in the last 3 pages
      if (currentPage >= totalPages - 2) {
        // Show last 3 pages without ellipsis (e.g., 6,7,8)
        for (let i = totalPages - 2; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show current page and next page (2 consecutive)
        pages.push(currentPage);
        pages.push(currentPage + 1);
        // Add ellipsis
        pages.push('...');
        // Add last 2 pages
        pages.push(totalPages - 1);
        pages.push(totalPages);
      }
    }

    return pages;
  };

  useEffect(() => {
    fetchFinancialStats();
    fetchTransactions();
  }, []);

  const fetchFinancialStats = async () => {
    try {
      const response = await axiosInstance.get(
        'api/admin/financial/statistics'
      );

      if (response.data.success && response.data.data) {
        const { volume, transactions } = response.data.data;
        setStats({
          totalDeposits: volume.totalDeposits || 0,
          totalWithdrawals: volume.totalWithdrawals || 0,
          totalExchanges: volume.totalExchanges || 0,
          pending: transactions.pending || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching financial statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axiosInstance.get(
        'api/admin/financial/transactions'
      );

      if (response.data.success && response.data.data) {
        setTransactions(response.data.data.transactions || []);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    }
  };

  const filteredTransactions = filterTransactions(
    transactions,
    searchTerm,
    filterType,
    filterStatus
  );

  // Pagination calculations
  const indexOfLastTransaction = currentPage * rowsPerPage;
  const indexOfFirstTransaction = indexOfLastTransaction - rowsPerPage;
  const currentTransactions = filteredTransactions.slice(
    indexOfFirstTransaction,
    indexOfLastTransaction
  );
  const totalPages = Math.ceil(filteredTransactions.length / rowsPerPage);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, Math.max(totalPages, 1)));
  }, [totalPages]);

  const fetchWalletBalances = async (uid) => {
    if (!uid || uid.trim() === '') {
      toast.error('Please enter a valid UID');
      setWalletBalances(null);
      return;
    }

    setFetchingBalances(true);
    setWalletBalances(null); // Reset previous data

    try {
      console.log('Fetching wallet balances for UID:', uid);

      const response = await axiosInstance.get(
        `/api/trading/wallet/balances?uid=${uid}`
      );

      console.log('API Response:', response);
      console.log('Response Data:', response.data);

      if (response.data && response.data.success) {
        console.log('Full Data:', response.data.data);
        console.log('Real Data:', response.data.data?.real);
        console.log(
          'Real Data Keys:',
          response.data.data?.real
            ? Object.keys(response.data.data.real)
            : 'NO REAL DATA'
        );
        console.log(
          'Real Data Stringified:',
          JSON.stringify(response.data.data?.real, null, 2)
        );

        if (
          response.data.data &&
          response.data.data.real &&
          Object.keys(response.data.data.real).length > 0
        ) {
          setWalletBalances(response.data.data);
          toast.success(
            `Wallet balances loaded! Found ${
              Object.keys(response.data.data.real).length
            } currencies`
          );
        } else {
          setWalletBalances(null);
          toast.warning('No real wallet data found for this UID');
        }
      } else {
        setWalletBalances(null);
        toast.error(response.data?.message || 'Failed to load wallet balances');
      }
    } catch (error) {
      console.error('Error fetching wallet balances:', error);
      console.error('Error response:', error.response);
      setWalletBalances(null);

      if (error.response?.status === 404) {
        toast.error('User not found with this UID');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(
          'Failed to fetch wallet balances. Check console for details.'
        );
      }
    } finally {
      setFetchingBalances(false);
    }
  };

  const handleLockBalance = async () => {
    if (!lockForm.uid || !lockForm.currencyCode || !lockForm.amount) {
      toast.error('Please fill in all fields');
      return;
    }

    if (parseFloat(lockForm.amount) <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }

    setLockLoading(true);
    try {
      const response = await axiosInstance.post(
        '/api/admin/wallets/lock-balance',
        {
          uid: lockForm.uid,
          currencyCode: lockForm.currencyCode,
          amount: parseFloat(lockForm.amount),
        }
      );

      if (response.data.success) {
        toast.success(response.data.message || 'Balance locked successfully!');
        const data = response.data.data;
        toast.info(
          `Locked: ${data.amountLocked} ${data.currencyCode}. Available: ${data.availableBalance}`,
          { autoClose: 5000 }
        );
        setLockForm({ uid: '', currencyCode: 'USDT', amount: '' });
        setShowLockModal(false);
      }
    } catch (error) {
      console.error('Error locking balance:', error);
      toast.error(error?.response?.data?.message || 'Failed to lock balance');
    } finally {
      setLockLoading(false);
    }
  };

  const handleUnlockBalance = async () => {
    if (!lockForm.uid || !lockForm.currencyCode || !lockForm.amount) {
      toast.error('Please fill in all fields');
      return;
    }

    if (parseFloat(lockForm.amount) <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }

    setLockLoading(true);
    try {
      const response = await axiosInstance.post(
        '/api/admin/wallets/unlock-balance',
        {
          uid: lockForm.uid,
          currencyCode: lockForm.currencyCode,
          amount: parseFloat(lockForm.amount),
        }
      );

      if (response.data.success) {
        toast.success(
          response.data.message || 'Balance unlocked successfully!'
        );
        const data = response.data.data;
        toast.info(
          `Unlocked: ${data.amountUnlocked} ${data.currencyCode}. Available: ${data.availableBalance}`,
          { autoClose: 5000 }
        );
        setLockForm({ uid: '', currencyCode: 'USDT', amount: '' });
        setShowLockModal(false);
      }
    } catch (error) {
      console.error('Error unlocking balance:', error);
      toast.error(error?.response?.data?.message || 'Failed to unlock balance');
    } finally {
      setLockLoading(false);
    }
  };

  const updateTransactionStatus = async (action) => {
    if (transactionActionLoading) return;
    setTransactionActionLoading(true);
    try {
      const response = await axiosInstance.post(
        'api/admin/financial/process-transaction',
        {
          transactionId: selectedTransaction.id,
          action: action === 'COMPLETED' ? 'APPROVE' : 'REJECT',
          note: `Transaction ${
            action === 'COMPLETED' ? 'approved' : 'rejected'
          }`,
        }
      );

      if (response.data.success) {
        toast.success(
          response.data.message ||
            `Transaction ${
              action === 'COMPLETED' ? 'approved' : 'rejected'
            } successfully!`
        );

        // Update local state
        setTransactions((prev) =>
          prev.map((txn) =>
            txn.id === selectedTransaction.id ? { ...txn, status: action } : txn
          )
        );

        setSelectedTransaction((prev) => ({ ...prev, status: action }));

        // Refresh data
        fetchTransactions();
        fetchFinancialStats();
      }
    } catch (error) {
      console.error('Error updating transaction:', error);
      toast.error(
        error?.response?.data?.message || 'Failed to update transaction status'
      );
    } finally {
      setTransactionActionLoading(false);
    }
  };

  const reopenTransactionAsPending = async () => {
    if (transactionActionLoading || selectedTransaction.status === 'PENDING') return;
    setTransactionActionLoading(true);
    try {
      const response = await axiosInstance.put(
        `api/admin/financial/transactions/${selectedTransaction.id}/pending`
      );
      if (response.data.success) {
        toast.success(response.data.message || 'Transaction reopened as pending');
        setTransactions((prev) => prev.map((txn) =>
          txn.id === selectedTransaction.id ? { ...txn, status: 'PENDING' } : txn
        ));
        setSelectedTransaction((prev) => ({ ...prev, status: 'PENDING' }));
        await Promise.all([fetchTransactions(), fetchFinancialStats()]);
      }
    } catch (error) {
      console.error('Error reopening transaction:', error);
      toast.error(error?.response?.data?.message || 'Failed to reopen transaction');
    } finally {
      setTransactionActionLoading(false);
    }
  };

  return (
    <div className='container-fluid transactions-page px-2 px-sm-3'>
      <h3 className='transactions-page__title'>Transaction Management</h3>
      {/* Statistics Cards */}
      <div className='transactions-summary'>
        <div>
          <div className='card transactions-summary__card bg-primary text-white text-center shadow-sm rounded-3'>
            <div className='card-body transactions-summary__body'>
              <h4>{loading ? '...' : `$${stats.totalDeposits.toFixed(2)}`}</h4>
              <p>Total Deposits</p>
            </div>
          </div>
        </div>
        <div>
          <div className='card transactions-summary__card bg-danger text-white text-center shadow-sm rounded-3'>
            <div className='card-body transactions-summary__body'>
              <h4>
                {loading ? '...' : `$${stats.totalWithdrawals.toFixed(2)}`}
              </h4>
              <p>Total Withdrawals</p>
            </div>
          </div>
        </div>
        <div>
          <div className='card transactions-summary__card bg-info text-white text-center shadow-sm rounded-3'>
            <div className='card-body transactions-summary__body'>
              <h4>{loading ? '...' : `$${stats.totalExchanges.toFixed(2)}`}</h4>
              <p>Total Exchanges</p>
            </div>
          </div>
        </div>
        <div>
          <div className='card transactions-summary__card bg-warning text-white text-center shadow-sm rounded-3'>
            <div className='card-body transactions-summary__body'>
              <h4>{loading ? '...' : stats.pending}</h4>
              <p>Pending Requests</p>
            </div>
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className='transactions-filters'>
        <div>
          <input
            type='text'
            className='form-control form-control-sm'
            placeholder='Search transactions...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div>
          <select
            className='form-select form-select-sm text-truncate'
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <option value='all'>All Types</option>
            <option value='DEPOSIT'>Deposits</option>
            <option value='WITHDRAWAL'>Withdrawals</option>
            <option value='EXCHANGE'>Exchanges</option>
          </select>
        </div>
        <div>
          <select
            className='form-select form-select-sm text-truncate'
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <option value='all'>All Status</option>
            <option value='PENDING'>Pending</option>
            <option value='COMPLETED'>Completed</option>
            <option value='FAILED'>Failed</option>
          </select>
        </div>
        <div>
          <button
            className='btn btn-sm btn-primary w-100'
            onClick={() => setShowLockModal(true)}
          >
            <Lock size={16} className='me-1' /> Lock/Unlock Balance
          </button>
        </div>
      </div>
      {/* Transactions Table */}
      <div className='card transactions-history shadow-sm border-0'>
        <div className='card-header bg-white border-bottom transactions-history__header'>
          <h5 className='mb-0 fw-semibold'>Transaction History</h5>
        </div>
        <div className='card-body p-0'>
          <div className='table-responsive'>
            <table className='table table-hover align-middle mb-0 transactions-table'>
              <thead className='table-light'>
                <tr>
                  <th className='text-start ps-3'>User</th>
                  <th className='text-start'>Type</th>
                  <th className='text-start'>Amount</th>
                  <th className='text-start'>Currency</th>
                  <th className='text-start'>Method</th>
                  <th className='text-start'>Status</th>
                  <th className='text-start'>Date</th>
                  <th className='text-start'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentTransactions.length > 0 ? (
                  currentTransactions.map((txn) => (
                    <tr
                      key={txn.id}
                      style={{ borderBottom: '1px solid #f0f0f0' }}
                    >
                      <td className='text-start ps-3'>
                        <div className='fw-semibold transactions-table__user'>
                          {txn.user?.fullName || txn.user?.email || 'N/A'}
                        </div>
                        <small className='text-muted transactions-table__uid'>
                          UID: {txn.user?.uid}
                        </small>
                      </td>
                      <td className='text-start'>
                        <span
                          className={`badge ${
                            txn.type === 'DEPOSIT'
                              ? 'bg-success'
                              : txn.type === 'WITHDRAWAL'
                              ? 'bg-danger'
                              : 'bg-info'
                          }`}
                        >
                          {txn.type.toLowerCase()}
                        </span>
                      </td>
                      <td className='text-start fw-semibold'>
                        ${txn.amount.toFixed(2)}
                      </td>
                      <td className='text-start'>{txn.currency}</td>
                      <td className='text-start'>{txn.method || 'N/A'}</td>
                      <td className='text-start'>
                        <span
                          className={`badge ${
                            txn.status === 'COMPLETED'
                              ? 'bg-success'
                              : txn.status === 'PENDING'
                              ? 'bg-warning'
                              : 'bg-danger'
                          }`}
                        >
                          {txn.status.toLowerCase()}
                        </span>
                      </td>
                      <td className='text-start'>
                        {new Date(txn.createdAt).toLocaleString()}
                      </td>
                      <td className='text-start'>
                        <button
                          className='btn btn-sm btn-primary rounded-pill px-2 transactions-table__manage'
                          onClick={() => setSelectedTransaction(txn)}
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan='8' className='text-center py-5 text-muted'>
                      {loading
                        ? 'Loading transactions...'
                        : 'No transactions found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {filteredTransactions.length > rowsPerPage && (
          <div className='card-footer bg-white border-top transactions-history__footer'>
            <div className='d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2'>
              <div className='text-muted small text-center text-sm-start'>
                Showing {indexOfFirstTransaction + 1} to{' '}
                {Math.min(indexOfLastTransaction, filteredTransactions.length)}{' '}
                of {filteredTransactions.length} transactions
              </div>
              <nav>
                <ul className='pagination pagination-sm mb-0'>
                  <li
                    className={`page-item ${
                      currentPage === 1 ? 'disabled' : ''
                    }`}
                  >
                    <button
                      className='page-link'
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                  </li>
                  {getPaginationNumbers().map((page, index) => {
                    if (page === '...') {
                      return (
                        <li
                          key={`ellipsis-${index}`}
                          className='page-item disabled'
                        >
                          <span className='page-link'>...</span>
                        </li>
                      );
                    }
                    return (
                      <li
                        key={page}
                        className={`page-item ${
                          currentPage === page ? 'active' : ''
                        }`}
                      >
                        <button
                          className='page-link'
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      </li>
                    );
                  })}
                  <li
                    className={`page-item ${
                      currentPage === totalPages ? 'disabled' : ''
                    }`}
                  >
                    <button
                      className='page-link'
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Transaction Detail Modal */}
      {selectedTransaction && (
        <div
          className='modal d-block'
          tabIndex='-1'
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setSelectedTransaction(null)}
        >
          <div
            className='modal-dialog modal-lg modal-dialog-centered'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='modal-content border-0 shadow-lg'>
              {/* Header */}
              <div
                className='modal-header text-white border-0'
                style={{
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                <h5 className='modal-title d-flex align-items-center gap-2'>
                  <FileText size={24} />
                  Transaction Details
                </h5>
                <button
                  type='button'
                  className='btn-close btn-close-white'
                  onClick={() => setSelectedTransaction(null)}
                ></button>
              </div>

              {/* Body */}
              <div className='modal-body p-4'>
                {/* Transaction ID Card */}
                <div className='card bg-light border-0 mb-4'>
                  <div className='card-body'>
                    <h6 className='card-title fw-bold mb-3'>Transaction ID</h6>
                    <div className='p-2 rounded bg-white border'>
                      <code className='text-primary'>
                        {selectedTransaction.id}
                      </code>
                    </div>
                  </div>
                </div>

                {/* Transaction Information Card */}
                <div className='card bg-light border-0 mb-4'>
                  <div className='card-body'>
                    <h6 className='card-title fw-bold mb-3'>
                      Transaction Information
                    </h6>
                    <div className='row g-3'>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>User Name</small>
                        <strong>
                          {selectedTransaction.user?.fullName ||
                            selectedTransaction.user?.email ||
                            'N/A'}
                        </strong>
                      </div>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>User UID</small>
                        <span className='badge bg-secondary'>
                          {selectedTransaction.user?.uid}
                        </span>
                      </div>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>Email</small>
                        <strong>{selectedTransaction.user?.email}</strong>
                      </div>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>Type</small>
                        <span
                          className={`badge ${
                            selectedTransaction.type === 'DEPOSIT'
                              ? 'bg-success'
                              : selectedTransaction.type === 'WITHDRAWAL'
                              ? 'bg-danger'
                              : 'bg-info'
                          }`}
                        >
                          {selectedTransaction.type}
                        </span>
                      </div>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>Amount</small>
                        <strong className='text-primary fs-5'>
                          ${selectedTransaction.amount?.toFixed(2)}
                        </strong>
                      </div>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>Currency</small>
                        <strong>{selectedTransaction.currency}</strong>
                      </div>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>Fee</small>
                        <strong>${selectedTransaction.fee}</strong>
                      </div>
                      {selectedTransaction.netAmount !== undefined && (
                        <div className='col-md-6'>
                          <small className='text-muted d-block'>
                            Net Amount
                          </small>
                          <strong className='text-success'>
                            ${selectedTransaction.netAmount?.toFixed(2)}
                          </strong>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Technical Details Card */}
                <div className='card border-primary'>
                  <div className='card-header bg-primary bg-opacity-10 border-primary'>
                    <h6 className='mb-0 fw-bold text-primary'>
                      Technical Details
                    </h6>
                  </div>
                  <div className='card-body'>
                    <div className='row g-3'>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>Method</small>
                        <strong>{selectedTransaction.method || 'N/A'}</strong>
                      </div>
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>Status</small>
                        <span
                          className={`badge ${
                            selectedTransaction.status === 'COMPLETED'
                              ? 'bg-success'
                              : selectedTransaction.status === 'PENDING'
                              ? 'bg-warning'
                              : 'bg-danger'
                          }`}
                        >
                          {selectedTransaction.status}
                        </span>
                      </div>
                      <div className='col-12'>
                        <small className='text-muted d-block'>
                          Wallet Address
                        </small>
                        {selectedTransaction.walletAddress ? (
                          <div className='p-2 rounded bg-light border mt-1'>
                            <code className='small text-break'>
                              {selectedTransaction.walletAddress}
                            </code>
                          </div>
                        ) : (
                          <span className='text-muted'>N/A</span>
                        )}
                      </div>
                      {selectedTransaction.externalTxId && (
                        <div className='col-12'>
                          <small className='text-muted d-block'>
                            External TX ID
                          </small>
                          <div className='p-2 rounded bg-light border mt-1'>
                            <code className='small text-break'>
                              {selectedTransaction.externalTxId}
                            </code>
                          </div>
                        </div>
                      )}
                      <div className='col-md-6'>
                        <small className='text-muted d-block'>Created At</small>
                        <strong>
                          {new Date(
                            selectedTransaction.createdAt
                          ).toLocaleString()}
                        </strong>
                      </div>
                      {selectedTransaction.processedAt && (
                        <div className='col-md-6'>
                          <small className='text-muted d-block'>
                            Processed At
                          </small>
                          <strong>
                            {new Date(
                              selectedTransaction.processedAt
                            ).toLocaleString()}
                          </strong>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className='modal-footer border-top'>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={() => setSelectedTransaction(null)}
                >
                  Close
                </button>
                {selectedTransaction.status !== 'PENDING' &&
                  ['DEPOSIT', 'WITHDRAWAL'].includes(selectedTransaction.type) && (
                  <button
                    type='button'
                    className='btn btn-warning'
                    disabled={transactionActionLoading}
                    onClick={reopenTransactionAsPending}
                  >
                    Set Pending
                  </button>
                )}
                <button
                  type='button'
                  className='btn btn-success'
                  disabled={transactionActionLoading || selectedTransaction.status !== 'PENDING'}
                  onClick={() => updateTransactionStatus('COMPLETED')}
                >
                  Approve
                </button>
                <button
                  type='button'
                  className='btn btn-danger'
                  disabled={transactionActionLoading || selectedTransaction.status !== 'PENDING'}
                  onClick={() => updateTransactionStatus('REJECTED')}
                >
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lock/Unlock Balance Modal */}
      {showLockModal && (
        <div
          className='modal d-block'
          tabIndex='-1'
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => {
            setShowLockModal(false);
            setLockForm({ uid: '', currencyCode: 'USDT', amount: '' });
            setWalletBalances(null);
          }}
        >
          <div
            className='modal-dialog modal-dialog-centered modal-lg'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='modal-content border-0 shadow-lg'>
              {/* Header */}
              <div
                className='modal-header text-white border-0'
                style={{
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                <h5 className='modal-title d-flex align-items-center gap-2'>
                  <Lock size={24} />
                  Lock/Unlock Currency
                </h5>
                <button
                  type='button'
                  className='btn-close btn-close-white'
                  onClick={() => {
                    setShowLockModal(false);
                    setLockForm({ uid: '', currencyCode: 'USDT', amount: '' });
                    setWalletBalances(null);
                  }}
                ></button>
              </div>

              {/* Body */}
              <div className='modal-body p-4'>
                {/* Info Alert */}
                <div className='alert alert-info d-flex align-items-start gap-2'>
                  <FileText size={20} className='mt-1 flex-shrink-0' />
                  <div>
                    <strong>Information</strong>
                    <p className='mb-0 small mt-1'>
                      Lock balance to temporarily freeze user funds. Use Unlock
                      to release them back to available balance.
                    </p>
                  </div>
                </div>

                {/* User UID Input */}
                <div className='mb-4'>
                  <label className='form-label fw-semibold'>
                    User UID <span className='text-danger'>*</span>
                  </label>
                  <div className='input-group'>
                    <input
                      type='text'
                      className='form-control'
                      placeholder='Enter user UID (e.g., 5305564)'
                      value={lockForm.uid}
                      onChange={(e) =>
                        setLockForm({ ...lockForm, uid: e.target.value })
                      }
                    />
                    <button
                      className='btn btn-primary'
                      type='button'
                      onClick={() => fetchWalletBalances(lockForm.uid)}
                      disabled={fetchingBalances || !lockForm.uid}
                    >
                      {fetchingBalances ? (
                        <>
                          <span className='spinner-border spinner-border-sm me-2' />
                          Searching...
                        </>
                      ) : (
                        <>
                          <Search size={18} className='me-1' /> Search
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Wallet Balances Display */}
                {walletBalances && (
                  <div className='card bg-light border-0 mb-4'>
                    <div className='card-body'>
                      <h6 className='card-title fw-bold mb-3'>
                        Wallet Balances - UID: {lockForm.uid}
                      </h6>

                      {walletBalances.real &&
                      Object.keys(walletBalances.real).length > 0 ? (
                        <div className='table-responsive'>
                          <table className='table table-sm table-bordered mb-0 bg-white'>
                            <thead className='table-light'>
                              <tr>
                                <th className='text-center'>Currency</th>
                                <th className='text-center'>Locked</th>
                                <th className='text-center'>Waiting</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(walletBalances.real).map(
                                ([currency, balance]) => (
                                  <tr key={currency}>
                                    <td className='text-center'>
                                      <span className='fw-bold text-primary'>
                                        {currency}
                                      </span>
                                    </td>
                                    <td className='text-center'>
                                      <span
                                        className={`fw-bold ${
                                          balance.locked > 0
                                            ? 'text-danger'
                                            : 'text-muted'
                                        }`}
                                      >
                                        {balance.locked}
                                      </span>
                                    </td>
                                    <td className='text-center'>
                                      <span
                                        className={`fw-bold ${
                                          balance.waiting > 0
                                            ? 'text-warning'
                                            : 'text-muted'
                                        }`}
                                      >
                                        {balance.waiting}
                                      </span>
                                    </td>
                                  </tr>
                                )
                              )}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className='alert alert-warning mb-0'>
                          <small>No wallet balances found for this user.</small>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Lock/Unlock Form */}
                <div className='card border-primary'>
                  <div className='card-header bg-primary bg-opacity-10 border-primary'>
                    <h6 className='mb-0 fw-bold text-primary'>
                      Lock/Unlock Details
                    </h6>
                  </div>
                  <div className='card-body'>
                    {/* Currency Code */}
                    <div className='mb-3'>
                      <label className='form-label fw-semibold'>
                        Currency Code <span className='text-danger'>*</span>
                      </label>
                      <select
                        className='form-select'
                        value={lockForm.currencyCode}
                        onChange={(e) =>
                          setLockForm({
                            ...lockForm,
                            currencyCode: e.target.value,
                          })
                        }
                      >
                        <option value='USDT'>USDT</option>
                        <option value='BTC'>BTC</option>
                        <option value='ETH'>ETH</option>
                        <option value='BNB'>BNB</option>
                        <option value='TRX'>TRX</option>
                      </select>
                    </div>

                    {/* Amount */}
                    <div className='mb-4'>
                      <label className='form-label fw-semibold'>
                        Amount <span className='text-danger'>*</span>
                      </label>
                      <input
                        type='number'
                        className='form-control'
                        placeholder='Enter amount to lock/unlock'
                        value={lockForm.amount}
                        onChange={(e) =>
                          setLockForm({ ...lockForm, amount: e.target.value })
                        }
                        min='0'
                        step='0.01'
                      />
                    </div>

                    {/* Summary */}
                    <div className='alert alert-secondary mb-0'>
                      <small>
                        <strong>Summary:</strong> UID:{' '}
                        <span className='badge bg-dark'>
                          {lockForm.uid || 'N/A'}
                        </span>{' '}
                        | Currency:{' '}
                        <span className='badge bg-primary'>
                          {lockForm.currencyCode}
                        </span>{' '}
                        | Amount:{' '}
                        <span className='badge bg-success'>
                          {lockForm.amount || '0'}
                        </span>
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className='modal-footer border-top'>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={() => {
                    setShowLockModal(false);
                    setLockForm({ uid: '', currencyCode: 'USDT', amount: '' });
                    setWalletBalances(null);
                  }}
                  disabled={lockLoading}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className='btn btn-danger'
                  onClick={handleLockBalance}
                  disabled={lockLoading}
                >
                  {lockLoading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' />
                      Locking...
                    </>
                  ) : (
                    <>
                      <Lock size={18} className='me-2' />
                      Lock Balance
                    </>
                  )}
                </button>
                <button
                  type='button'
                  className='btn btn-success'
                  onClick={handleUnlockBalance}
                  disabled={lockLoading}
                >
                  {lockLoading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' />
                      Unlocking...
                    </>
                  ) : (
                    <>
                      <Unlock size={18} className='me-2' />
                      Unlock Balance
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTransactions;
